"use client";

import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Image as ImageIcon, Loader2, Trash2, Video } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import Button from "@/components/ui/button/Button";
import Label from "@/components/form/Label";
import { resolveMediaUrl } from '@/utils/mediaUrl';

export interface EditField {
  key: string;
  label: string;
  type?: "text" | "number" | "select" | "textarea" | "multiselect" | "checkbox" | "datetime-local" | "file" | "color";
  options?: { value: string; label: string }[];
  required?: boolean;
  placeholder?: string;
  /** Shown under the field — used for encoding conventions (e.g. "one per line"). */
  hint?: string;
  /** type: "file" only — uploads the picked file and returns the URL to store in values[key],
   * same "upload, then fill the URL field" flow as ProductMediaController's hero-slides/
   * campaigns endpoints (no id to attach a child row to yet at form-fill time). */
  uploadFn?: (file: File) => Promise<string>;
  /** type: "file" only — same contract as `uploadFn` but reports real upload progress (0-100)
   * via XHR instead of `fetch`. Takes priority over `uploadFn` when both are given. */
  uploadWithProgressFn?: (file: File, onProgress: (pct: number) => void) => Promise<string>;
  /** type: "file" only — <input accept>, e.g. "image/jpeg,image/png,image/gif,image/webp". */
  accept?: string;
  /** type: "file" only — thumbnail style; defaults to "image". */
  previewKind?: "image" | "video";
  /** type: "file" only — client-side size cap in MB, checked before upload starts. */
  maxSizeMB?: number;
  /** type: "file" only — client-side minimum pixel dimensions (image only), checked before upload starts. */
  minDimensions?: { width: number; height: number };
}

/** type: "file" field body — shows the current URL (image thumbnail or a video placeholder)
 * plus an upload button; the upload's returned URL is written into the form value like any
 * other field, so Save still submits a plain string. */
/** Reads natural pixel dimensions via a throwaway <img>, without ever attaching it to the DOM. */
function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read the image file."));
    };
    img.src = url;
  });
}

function FileUploadField({ field, value, onChange, disabled }: {
  field: EditField;
  value: string;
  onChange: (url: string) => void;
  disabled: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = async (file: File): Promise<string | null> => {
    if (field.maxSizeMB && file.size > field.maxSizeMB * 1024 * 1024) {
      return `File is too large — max ${field.maxSizeMB}MB.`;
    }
    if (field.minDimensions) {
      try {
        const { width, height } = await readImageDimensions(file);
        if (width < field.minDimensions.width || height < field.minDimensions.height) {
          return `Image is too small — minimum ${field.minDimensions.width}×${field.minDimensions.height}px (got ${width}×${height}).`;
        }
      } catch {
        return "Could not read the image file. Please try a different file.";
      }
    }
    return null;
  };

  const handleFile = async (file: File | undefined) => {
    if (!file || (!field.uploadFn && !field.uploadWithProgressFn)) return;
    setError("");

    const validationError = await validate(file);
    if (validationError) {
      setError(validationError);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const url = field.uploadWithProgressFn
        ? await field.uploadWithProgressFn(file, setProgress)
        : await field.uploadFn!(file);
      onChange(url);
    } catch (err) {
      setError((err as Error)?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (disabled || uploading) return;
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="mt-1 space-y-2">
      {value ? (
        <div className="flex items-center gap-2">
          {field.previewKind === "video" ? (
            <div className="flex h-10 w-16 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-400 dark:bg-gray-700">
              <Video className="h-4 w-4" />
            </div>
          ) : (
            <img src={resolveMediaUrl(value)} alt="" className="h-10 w-16 shrink-0 rounded-md object-cover bg-gray-100 dark:bg-gray-700" />
          )}
          <span className="flex-1 truncate text-xs text-gray-500" title={value}>{value}</span>
          {confirmingRemove ? (
            <div className="flex shrink-0 items-center gap-2 text-xs">
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" /> Remove?
              </span>
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setConfirmingRemove(false);
                }}
                disabled={disabled || uploading}
                className="text-red-500 hover:underline disabled:opacity-40"
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setConfirmingRemove(false)}
                disabled={disabled || uploading}
                className="text-gray-500 hover:underline disabled:opacity-40"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingRemove(true)}
              disabled={disabled || uploading}
              className="flex shrink-0 items-center gap-1 text-xs text-red-500 hover:underline disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
      ) : null}
      <label
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !uploading) setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        className={`flex cursor-pointer flex-col gap-2 rounded-lg border border-dashed px-3 py-3 text-sm transition-colors ${
          dragActive
            ? "border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
            : "border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:border-gray-500"
        }`}
      >
        <div className="flex items-center gap-1">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : field.previewKind === "video" ? <Video className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
          {uploading
            ? field.uploadWithProgressFn
              ? `Uploading… ${progress}%`
              : "Uploading…"
            : value
              ? "Replace (or drag & drop)"
              : "Upload (or drag & drop)"}
        </div>
        {uploading && field.uploadWithProgressFn && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={field.accept}
          disabled={disabled || uploading}
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
        />
      </label>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  fields: EditField[];
  initialValues?: Record<string, any>;
  onSubmit: (values: Record<string, any>) => Promise<void>;
  submitLabel?: string;
  /** Tailwind max-width class for the modal panel — defaults to "max-w-3xl" (768px, the
   * standard edit-modal width across the admin dashboard; was "max-w-md"/448px). */
  maxWidthClass?: string;
}

/**
 * Config-driven create/edit form modal, reused across every admin-dashboard module so each
 * page doesn't hand-roll its own modal shell. Wraps the existing `Modal`/`Button`/`Label`
 * primitives already used by RoleUpdateModal.
 */
export default function EditModal({
  isOpen,
  onClose,
  title,
  description,
  fields,
  initialValues,
  onSubmit,
  submitLabel = "Save",
  maxWidthClass = "max-w-3xl",
}: EditModalProps) {
  const [values, setValues] = useState<Record<string, any>>(initialValues ?? {});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Reset only on the closed->open transition, not on every re-render while
  // already open. `initialValues` is commonly passed as an inline object
  // literal by callers (e.g. `initialValues={{ status: 'Draft' }}`), which is
  // a new reference on every parent render — including this component in the
  // effect's dependency array would silently wipe whatever the user had
  // already typed the next time the parent re-rendered for any unrelated
  // reason (a background query refetch, an unrelated state update, etc.).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setValues(initialValues ?? {});
      setError("");
    }
    wasOpen.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const setField = (key: string, value: any) => setValues((prev) => ({ ...prev, [key]: value }));

  const toggleMultiselect = (key: string, optionValue: string) => {
    setValues((prev) => {
      const current: string[] = Array.isArray(prev[key]) ? prev[key] : [];
      const next = current.includes(optionValue)
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue];
      return { ...prev, [key]: next };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const missing = fields.find((f) => {
      if (!f.required) return false;
      if (f.type === "multiselect") return !(Array.isArray(values[f.key]) && values[f.key].length > 0);
      if (f.type === "checkbox") return false; // false is a valid, complete value
      return !String(values[f.key] ?? "").trim();
    });
    if (missing) {
      setError(`${missing.label} is required`);
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      setError((err as Error)?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className={maxWidthClass}>
      <div className={`relative w-full ${maxWidthClass} overflow-hidden rounded-2xl bg-white p-6 dark:bg-gray-900`}>
        <div className="mb-6">
          <h4 className="text-xl font-semibold text-gray-800 dark:text-white/90">{title}</h4>
          {description && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="max-h-[65vh] overflow-y-auto pr-1 space-y-4">
            {fields.map((field) => (
              <div key={field.key}>
                <Label htmlFor={field.key} required={field.required}>
                  {field.label}
                </Label>
                {field.type === "select" ? (
                  <select
                    id={field.key}
                    value={values[field.key] ?? ""}
                    onChange={(e) => setField(field.key, e.target.value)}
                    disabled={submitting}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    {field.options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : field.type === "multiselect" ? (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 space-y-1">
                    {field.options?.length ? (
                      field.options.map((opt) => {
                        const selected: string[] = Array.isArray(values[field.key]) ? values[field.key] : [];
                        const checked = selected.includes(opt.value);
                        return (
                          <label
                            key={opt.value}
                            className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-700 dark:text-gray-200"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={submitting}
                              onChange={() => toggleMultiselect(field.key, opt.value)}
                              className="rounded border-gray-300 dark:border-gray-600"
                            />
                            {opt.label}
                          </label>
                        );
                      })
                    ) : (
                      <p className="text-xs text-gray-400 px-2 py-1">No options available.</p>
                    )}
                  </div>
                ) : field.type === "textarea" ? (
                  <textarea
                    id={field.key}
                    value={values[field.key] ?? ""}
                    onChange={(e) => setField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    disabled={submitting}
                    rows={3}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                ) : field.type === "checkbox" ? (
                  <label className="mt-1 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      id={field.key}
                      type="checkbox"
                      checked={!!values[field.key]}
                      disabled={submitting}
                      onChange={(e) => setField(field.key, e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    {field.placeholder}
                  </label>
                ) : field.type === "file" ? (
                  <FileUploadField
                    field={field}
                    value={values[field.key] ?? ""}
                    onChange={(url) => setField(field.key, url)}
                    disabled={submitting}
                  />
                ) : field.type === "color" ? (
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={values[field.key] || "#000000"}
                      onChange={(e) => setField(field.key, e.target.value)}
                      disabled={submitting}
                      className="h-10 w-14 shrink-0 cursor-pointer rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-1"
                    />
                    <input
                      id={field.key}
                      type="text"
                      value={values[field.key] ?? ""}
                      onChange={(e) => setField(field.key, e.target.value)}
                      placeholder={field.placeholder || "#059669"}
                      disabled={submitting}
                      className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                ) : (
                  <input
                    id={field.key}
                    type={field.type === "number" ? "number" : field.type === "datetime-local" ? "datetime-local" : "text"}
                    value={values[field.key] ?? ""}
                    onChange={(e) =>
                      setField(field.key, field.type === "number" ? e.target.valueAsNumber : e.target.value)
                    }
                    placeholder={field.placeholder}
                    disabled={submitting}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                )}
                {field.hint && <p className="mt-1 text-xs text-gray-400">{field.hint}</p>}
              </div>
            ))}

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
          </div>

          <div className="mt-8 flex items-center justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting}>
              {submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
